// 计费相关接口的类型与访问函数：余额与按日增减、用量汇总（docs/04 第 4、6、7 节）。字段名同表列名。
import { api, download } from "./api";
import { formatTime } from "@/ui";
import type { ListCount } from "./usePage";

export type LedgerType = "topup" | "charge" | "adjustment" | "transfer_in" | "transfer_out" | "correction" | "carry_forward";

export interface TopupInput { amount: number; ref: string; note: string }
export interface TopupResult { account_id: number; balance: number; currency: string; counter: number }

/** 余额列表的一行：消耗是扣费合计取正，按北京日（决策 D99）。 */
export interface BalanceSummary { account_id: number; customer_id: number; balance: number; currency: string; today: number; yesterday: number; month: number }

/** 一个北京日的余额增减：amounts 按流水类型给当天的合计（有符号），balance 是日末余额，今天是到现在为止。 */
export interface BalanceDay { day: string; amounts: Partial<Record<LedgerType, number>>; net: number; balance: number }

/** 流水类型在页面上的名称（交易类型）：扣费显示为消耗。 */
export const LEDGER_TYPE: Record<LedgerType, string> = { charge: "消耗", topup: "充值", correction: "价格更正", adjustment: "调整", transfer_in: "转入", transfer_out: "转出", carry_forward: "归档结转" };

export const balances = {
  list: () => api<BalanceSummary[]>("/balances"),
  /** from 到 to 的每个北京日（YYYY-MM-DD，含两端），最新的在前。 */
  days: (accountId: number, from: string, to: string) => api<BalanceDay[]>(`/accounts/${accountId}/balance/days?${new URLSearchParams({ from, to })}`),
  topup: (accountId: number, body: TopupInput) => api<TopupResult>(`/accounts/${accountId}/topup`, { method: "POST", body: JSON.stringify(body) }),
};


/** 报表的周期：10 分钟、小时、日、月，按北京时间切分（D197）。 */
export type ReportPeriod = "10m" | "hour" | "day" | "month";
/** 报表的聚合维度（D197，同通道健康页 D186）：entity 是账号或通道，op 是 MCC+MNC；选中的合起来作为聚合键，都不选是明细。 */
export type ReportDim = "entity" | "mcc" | "op";

/** 全部聚合维度，也是接口 group 参数的写法和顺序。 */
export const REPORT_DIMS: ReportDim[] = ["entity", "mcc", "op"];

/** 每种周期最长可查的天数，同服务端（docs/04 第 12 节）。 */
export const REPORT_SPAN_DAYS: Record<ReportPeriod, number> = { "10m": 7, hour: 92, day: 366, month: 366 };

/** 报表的筛选条件：from、to 是 RFC 3339 时刻，按 10 分钟时段对齐。 */
export interface ReportFilter { from: string; to: string; period: ReportPeriod; group: ReportDim[]; entityId: string; ownerId: string }

/** 报表的一行，period 是周期起点；所有数量为段数，价格和金额为微单位（D107）。
 * 聚合时单价为 null；entities、owners、mccs、operators 是这一行涉及的个数，多于一个时对应的维度列显示个数（D197）。 */
export interface ReportRow {
  period: string; mcc: string; mnc: string; entity_id: number; owner_id: number;
  entities: number; owners: number; mccs: number; operators: number;
  submitted: number; delivered: number; billed: number;
  unit_price: number | null; currency: string; amount: number | null;
  /** 营收数据的成本与利润（D195），成本报表为 null；币种未知（XXX）的行是基准币种。 */
  cost: number | null; profit: number | null;
}

/** 报表全部筛选结果按币种的合计（D195），不含未定价的行（D197）。 */
export interface ReportTotal {
  currency: string; submitted: number; delivered: number; billed: number;
  amount: number | null; cost: number | null; profit: number | null;
}

/** 两个独立报表的接口名称，分别对应账号和通道（D107）。 */
export type ReportSide = "customers" | "suppliers";

/** 分页读取持久报表，不访问原始消息或当前价格。 */
export const reports = {
  list: (side: ReportSide, f: ReportFilter, page: { limit: number; offset: number }) => {
    const q = reportParams(f);
    q.set("limit", String(page.limit));
    q.set("offset", String(page.offset));
    return api<ReportRow[]>(`/reports/${side}?${q}`);
  },
  /** 报表的行数，条件同列表（决策 D182）。 */
  count: (side: ReportSide, f: ReportFilter) => api<ListCount>(`/reports/${side}/count?${reportParams(f)}`),
  /** 全部筛选结果按币种的合计，与周期、聚合无关（D195）。 */
  total: (side: ReportSide, f: ReportFilter) => api<ReportTotal[]>(`/reports/${side}/total?${reportParams({ ...f, period: "day", group: [] })}`),
  /** 营收数据连同合计导出成 Excel（D195）。 */
  exportRevenue: (f: ReportFilter) => download(`/reports/customers/export?${reportParams(f)}`, `营收数据_${fileStamp(f.from)}_${fileStamp(f.to)}.xlsx`),
};

/** 报表的筛选条件转成查询串。 */
function reportParams(f: ReportFilter) {
  const q = new URLSearchParams({ from: f.from, to: f.to, period: f.period });
  if (f.group.length) q.set("group", f.group.join(","));
  if (f.entityId) q.set("entity_id", f.entityId);
  if (f.ownerId) q.set("owner_id", f.ownerId);
  return q;
}

/** 文件名里的北京时间，如 20261014-1300。 */
function fileStamp(iso: string) {
  return formatTime(iso, false).replace(/-/g, "").replace(" ", "-").replace(":", "");
}

/** 周期起点在表格里的写法（北京时间）：10 分钟与小时到分钟，日到日，月到月。 */
export function periodLabel(iso: string, period: ReportPeriod): string {
  const t = formatTime(iso, false);
  return period === "day" ? t.slice(0, 10) : period === "month" ? t.slice(0, 7) : t;
}

/** 每计费段的金额（均价、成本单价，D197），四舍五入到微单位；金额未知或没有计费时为 null。 */
export function perBilled(micro: number | null, billed: number): number | null {
  return micro === null || billed === 0 ? null : Math.round(micro / billed);
}

/** 毛利率：利润除以收入，保留一位小数；收入未知或为零时为 null（D197）。 */
export function marginText(profit: number | null, amount: number | null): string | null {
  return profit === null || !amount ? null : `${((profit / amount) * 100).toFixed(1)}%`;
}

export type PriceKind = "sell" | "cost";

export const PRICE_KIND: Record<PriceKind, string> = { sell: "客户价格", cost: "通道价格" };
